import SectionPanel from '@/components/SectionPanel';
import { Button } from '@/components/ui/button';
import { EditIcon } from '@/components/icons/Icon';
import CountryFlag from '@/components/CountryFlag';
import WaitingSpinner from '@/components/WaitingSpinner';
import { Partner } from '@/api/Partner';
import { partnerService } from '@/service/PartnerService';
import { countries } from '@/data/Countries';
import { formatOverview } from '@/data/DateTimeUtil';
import { Box, DataList, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import Content from '@/components/Content';
import PartnerDetailTabs from './PartnerDetailTabs';
import { useI18n } from '@/i18n/I18nContext';

interface PartnerDetailsViewProps {
    partnerId: number;
    onEdit: () => void;
    onCreateUser: () => void;
}

const valueOrDash = (value?: string | null): string => value && value.trim().length > 0 ? value : '-';

const PartnerDetailsView = ({ partnerId, onEdit }: PartnerDetailsViewProps) => {
    const { t } = useI18n();
    const [partner, setPartner] = useState<Partner | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        setIsLoading(true);
        setServerError('');
        partnerService
            .find(partnerId)
            .then(p => setPartner(p))
            .catch(() => setServerError('Partner konnte nicht geladen werden.'))
            .finally(() => setIsLoading(false));
    }, [partnerId]);

    if (isLoading) {
        return (
            <Stack py={8} align='center'>
                <WaitingSpinner size='xl' />
                <Text color='fg.muted'>Partner wird geladen...</Text>
            </Stack>
        );
    }

    if (serverError) {
        return <Text color='red.500' py={6}>{serverError}</Text>;
    }

    if (!partner) {
        return null;
    }

    return (
        <Content title={partner.name} description={t('partners.details.description')}
            actions={<Button colorPalette='blue' variant='outline' onClick={onEdit}>
                <EditIcon /> {t('partners.details.edit')}
            </Button>}>
            <Box maxW='6xl' mx='auto' minW='0'>
                <PartnerDetailTabs partnerId={partnerId}>
                    <SimpleGrid columns={{ base: 1, xl: 2 }} gap='6' pt='4' minW='0'>
                        <SectionPanel title={t('partners.details.information')}>
                            <DataList.Root orientation='horizontal' divideY='1px' gap={3}
                                css={{
                                    '& > div': { alignItems: 'start', flexDirection: { base: 'column', md: 'row' } },
                                    '& dt': { minWidth: { base: 0, md: '9rem' }, flexShrink: 0 },
                                    '& dd': { minWidth: 0, overflowWrap: 'anywhere' },
                                }}>
                                <DataList.Item>
                                    <DataList.ItemLabel>Typ</DataList.ItemLabel>
                                    <DataList.ItemValue>{partner.consultant ? 'Konsulent' : 'Partner'}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Land</DataList.ItemLabel>
                                    <DataList.ItemValue>
                                        <HStack gap={2}>
                                            <CountryFlag code={partner.country} />
                                            <Text>{countries[partner.country] ?? partner.country}</Text>
                                        </HStack>
                                    </DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Währung</DataList.ItemLabel>
                                    <DataList.ItemValue>{partner.currency}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>E-Mail</DataList.ItemLabel>
                                    <DataList.ItemValue>{valueOrDash(partner.email)}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Erstellt</DataList.ItemLabel>
                                    <DataList.ItemValue>{formatOverview(partner.createdAt)}</DataList.ItemValue>
                                </DataList.Item>
                            </DataList.Root>
                        </SectionPanel>
                        <SectionPanel title={t('partners.details.bankDetails')}>
                            <DataList.Root orientation='horizontal' divideY='1px' gap={3}
                                css={{
                                    '& > div': { alignItems: 'start', flexDirection: { base: 'column', md: 'row' } },
                                    '& dt': { minWidth: { base: 0, md: '9rem' }, flexShrink: 0 },
                                    '& dd': { minWidth: 0, overflowWrap: 'anywhere' },
                                }}>
                                <DataList.Item>
                                    <DataList.ItemLabel>Bank</DataList.ItemLabel>
                                    <DataList.ItemValue>{partner.bankName}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Konto-Inhaber</DataList.ItemLabel>
                                    <DataList.ItemValue>{partner.accountName}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Konto Nr</DataList.ItemLabel>
                                    <DataList.ItemValue>{partner.accountNumber}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>SWIFT Code</DataList.ItemLabel>
                                    <DataList.ItemValue>{partner.swiftCode}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Bank Code</DataList.ItemLabel>
                                    <DataList.ItemValue>{valueOrDash(partner.bankCode)}</DataList.ItemValue>
                                </DataList.Item>
                            </DataList.Root>
                        </SectionPanel>
                    </SimpleGrid>
                </PartnerDetailTabs>
            </Box>
        </Content>
    );
};

export default PartnerDetailsView;
